import type z from "zod";
import { getBaseURL } from "./getBaseUrl";
import { type Result, err, ok } from "neverthrow";

export type RequestConfig<ResponseSchema extends z.ZodType | undefined> = {
  responseSchema: ResponseSchema;
  request: Request;
};

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS" | "HEAD";

export class RequestBuilder<
  PathParamsSchema extends z.ZodType | undefined = undefined,
  BodySchema extends z.ZodType | undefined = undefined,
  ResponseSchema extends z.ZodType | undefined = undefined,
> {
  private url: URL;

  private pathParamsSchema?: PathParamsSchema = undefined;
  private bodySchema?: BodySchema = undefined;
  private responseSchema?: ResponseSchema = undefined;

  private method: Method = "GET";
  private headers: Headers = new Headers();
  private path: (query: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined) => string;

  constructor(opts: {
    path: (query: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined) => string;
    paramsSchema?: PathParamsSchema;
    bodySchema?: BodySchema;
    responseSchema?: ResponseSchema;
  }) {
    this.url = new URL(getBaseURL());
    this.pathParamsSchema = opts.paramsSchema;
    this.bodySchema = opts.bodySchema;
    this.responseSchema = opts.responseSchema;
    this.path = opts.path;

    this.withContentType("application/json");
  }

  withHost(host: string) {
    this.url.host = host;
    return this;
  }

  withMethod(method: Method) {
    this.method = method;
    return this;
  }

  withContentType(contentType: "application/json" | "text/plain" | string | undefined) {
    if (!contentType) {
      return this.withRemoveHeader("Content-Type");
    }
    return this.withHeader("Content-Type", contentType);
  }

  withRemoveHeader(name: string) {
    this.headers.delete(name);
    return this;
  }

  withHeader(name: string, value: string) {
    this.headers.set(name, value);
    return this;
  }

  withHeaders(headers: Record<string, string>) {
    Object.entries(headers).forEach(([name, value]) => this.withHeader(name, value));
    return this;
  }

  withBearerToken(token: string | undefined) {
    if (!token) {
      return this;
    }
    return this.withHeader("Authorization", `Bearer ${token}`);
  }

  build(
    params: PathParamsSchema extends z.ZodType ? z.infer<PathParamsSchema> : undefined,
    body: BodySchema extends z.ZodType ? z.infer<BodySchema> : undefined,
  ): Result<RequestConfig<ResponseSchema>, Error> {
    if (this.bodySchema) {
      const p = this.bodySchema.safeParse(body);
      if (!p.success) {
        return err(new Error(`Invalid body: ${p.error.message}`));
      }
    }
    if (this.pathParamsSchema) {
      const p = this.pathParamsSchema.safeParse(params);
      if (!p.success) {
        return err(new Error(`Invalid params: ${p.error.message}`));
      }
    }

    this.url.pathname = this.path(params);
    return ok({
      responseSchema: this.responseSchema as ResponseSchema,
      request: new Request(this.url.toString(), {
        method: this.method,
        headers: this.headers,
        body: body ? JSON.stringify(body) : undefined,
      }),
    });
  }
}
